import { ButtonStyles, EmptyStateIcons, Table, Button, Popup } from '@silverfort/sf-components';
import React, { useEffect } from 'react';
import { ComponentTableColumn, DcComponent, NodeComponent, PushServiceComponent, SoftwareStatus } from './types';
import { isUserPermitted, removeTrailingTags } from '../../utils';
import { handleAutoDcUpgrade, NODES_TYPES } from './EnvironmentOverviewUtils';
import {
    buildComponentsOverviewFilters,
    buildDisplayedComponents,
    calcStatus,
    getBaseColumns,
    getComponents
} from './ComponentsOverviewUtils';
import { ROLES } from '@silverfort-nodelibs/common/utils/consts';
import { useGetUserData } from '../../../api';
import { getDcsColumns } from './updateWizard/Steps/UpdateComponentsStepUtils';

export interface ComponentsOverviewProps {
    softwareStatus: SoftwareStatus;
    serversData: {
        nodes: NodeComponent[];
        dcs: DcComponent[];
        pushServices: PushServiceComponent[];
    };
    selectedTab: string;
    setSoftwareStatus?: (status: SoftwareStatus) => void;
}

const DEFAULT_FILTERS = {
    version: [],
    is_protected: []
};

const ComponentsOverview: React.FC<ComponentsOverviewProps> = ({
    softwareStatus,
    serversData,
    selectedTab,
    setSoftwareStatus
}) => {
    const [displayedComponents, setDisplayedComponents] = React.useState<ComponentTableColumn[]>([]);
    const [searchTerm, setSearchTerm] = React.useState<string>('');
    const [sortSettings, setSortSettings] = React.useState<{ sortBy: string; direction: 'asc' | 'desc' }>({
        sortBy: 'name',
        direction: 'asc'
    });
    const [selectedFilters, setSelectedFilters] = React.useState<{ [key: string]: any[] }>(DEFAULT_FILTERS);
    const [showDcPopup, setShowDcPopup] = React.useState<boolean>(false);
    const { userSession } = useGetUserData();
    const isEditor = userSession && isUserPermitted(userSession.user, 'softwareUpdates', ROLES.EDITOR.value);

    useEffect(() => {
        setSelectedFilters(DEFAULT_FILTERS);
        setSearchTerm('');
        setSortSettings({ sortBy: 'name', direction: 'asc' });
    }, [selectedTab]);

    useEffect(() => {
        const components = getComponents(selectedTab, serversData);
        const componentsType = selectedTab;
        const tableComponents = buildDisplayedComponents({
            searchTerm,
            selectedFilters,
            sortSettings,
            components,
            softwareStatus,
            componentsType
        });

        setDisplayedComponents(
            tableComponents.map((comp, i) => ({
                name: comp.name,
                version: removeTrailingTags(comp.version),
                connected: comp.connected,
                status: calcStatus(comp.connected, comp.version, comp.inventoryName, softwareStatus, componentsType),
                type: comp.type,
                az: comp.az || [],
                domains: comp.domains || [],
                inventoryName: comp.inventoryName,
                protected: comp.is_protected || false
            }))
        );
    }, [selectedTab, serversData, searchTerm, softwareStatus, sortSettings, selectedFilters]);

    const components = getComponents(selectedTab, serversData);
    return (
        <div className="h-full flex gap-6 flex-col">
            {showDcPopup && (
                <Popup
                    className=""
                    open={showDcPopup}
                    title="Disable Automatic DC Upgrades"
                    ContentComponent={() => (
                        <div className="text-14 text-sf-gray-700">
                            Disabling DC automatic upgrade means that you will need to manually upgrade your Domain
                            Controllers after each Silverfort Tower upgrade. Are you sure you want to proceed?
                        </div>
                    )}
                    FooterComponent={() => (
                        <div className="flex justify-end items-center gap-2">
                            <Button
                                buttonStyle={ButtonStyles.Tertiary}
                                text="Cancel"
                                onClick={() => setShowDcPopup(false)}></Button>
                            <Button
                                buttonStyle={ButtonStyles.PrimarySolid}
                                text="Disable"
                                onClick={() => {
                                    handleAutoDcUpgrade(false, setSoftwareStatus);
                                    setShowDcPopup(false);
                                }}></Button>
                        </div>
                    )}
                    onClose={() => setShowDcPopup(false)}
                />
            )}
            {components?.length > 0 &&
                buildComponentsOverviewFilters({
                    components,
                    setSelectedFilters,
                    selectedFilters,
                    selectedTab,
                    softwareStatus,
                    handleAutoDcUpgrade,
                    setShowDcPopup,
                    setSearchTerm,
                    setSoftwareStatus,
                    isEditor,
                    searchTerm
                })}

            <Table
                onSortChange={(col, direction) => setSortSettings({ sortBy: col as string, direction })}
                columns={(selectedTab === NODES_TYPES.DCS
                    ? getDcsColumns(softwareStatus)
                    : getBaseColumns(softwareStatus)
                ).filter((col) => (softwareStatus.versionUpgradeInProgress ? true : col.columnKey !== 'status'))}
                rows={displayedComponents}
                withScrollBar={true}
                EmptyStateProps={{
                    icon: EmptyStateIcons.NoItems,
                    title: `${
                        searchTerm || Object.values(selectedFilters).find((filter) => filter.length)
                            ? 'No results found. Try adjusting your filters.'
                            : 'Components haven’t been configured'
                    }`
                }}
            />
        </div>
    );
};

export default ComponentsOverview;
